import { useEffect, useRef } from 'react';

interface Star {
    x: number;
    y: number;
    z: number;
    size: number;
    color: { r: number; g: number; b: number };
}

interface Shape {
    x: number;
    y: number;
    z: number;
    size: number;
    color: { r: number; g: number; b: number };
    type: 'cube' | 'sphere' | 'cylinder' | 'pyramid';
    rotation: number;
    rotationSpeed: number;
}

interface StarFieldProps {
    className?: string;
    starCount?: number;
    speed?: number;
}

// Vibrant star colors
const STAR_COLORS = [
    { r: 100, g: 200, b: 255 },  // Cyan
    { r: 80, g: 140, b: 255 },   // Blue
    { r: 180, g: 100, b: 255 },  // Purple
    { r: 255, g: 150, b: 50 },   // Orange
    { r: 255, g: 220, b: 80 },   // Yellow
    { r: 255, g: 100, b: 100 },  // Red
    { r: 255, g: 80, b: 150 },   // Pink
    { r: 255, g: 255, b: 255 },  // White
    { r: 255, g: 255, b: 255 },  // White (more common)
    { r: 200, g: 220, b: 255 },  // Light blue
];

const SHAPE_TYPES: Shape['type'][] = ['cube', 'sphere', 'cylinder', 'pyramid'];

export function StarField({ className = '', starCount = 400, speed = 0.5 }: StarFieldProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const starsRef = useRef<Star[]>([]);
    const shapesRef = useRef<Shape[]>([]);
    const animationRef = useRef<number>(0);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Set canvas to full size
        const resizeCanvas = () => {
            const dpr = window.devicePixelRatio || 1;
            const rect = canvas.getBoundingClientRect();
            canvas.width = rect.width * dpr;
            canvas.height = rect.height * dpr;
            ctx.scale(dpr, dpr);
            canvas.style.width = `${rect.width}px`;
            canvas.style.height = `${rect.height}px`;
        };

        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        // Initialize stars with random positions in 3D space
        const initStars = () => {
            const stars: Star[] = [];
            for (let i = 0; i < starCount; i++) {
                const color = STAR_COLORS[Math.floor(Math.random() * STAR_COLORS.length)];
                stars.push({
                    x: Math.random() * canvas.width - canvas.width / 2,
                    y: Math.random() * canvas.height - canvas.height / 2,
                    z: Math.random() * 1000 + 1,
                    size: Math.random() * 2 + 0.5,
                    color,
                });
            }
            starsRef.current = stars;

            // Initialize a few shapes
            const shapes: Shape[] = [];
            for (let i = 0; i < 8; i++) {
                shapes.push(createShape(canvas.width, canvas.height));
            }
            shapesRef.current = shapes;
        };

        const createShape = (width: number, height: number, atBack = false): Shape => {
            // Spawn shapes centered but at random depths like stars
            const spread = 0.4; // How spread out from center (0-1)
            return {
                x: (Math.random() - 0.5) * width * spread,
                y: (Math.random() - 0.5) * height * spread,
                z: atBack ? 1000 : Math.random() * 1000 + 1, // Random depth like stars, or at back when respawning
                size: Math.random() * 30 + 20,
                color: STAR_COLORS[Math.floor(Math.random() * STAR_COLORS.length)],
                type: SHAPE_TYPES[Math.floor(Math.random() * SHAPE_TYPES.length)],
                rotation: Math.random() * Math.PI * 2,
                rotationSpeed: (Math.random() - 0.5) * 0.05,
            };
        };

        const drawShape = (shape: Shape, screenX: number, screenY: number, size: number, depth: number) => {
            const { r, g, b } = shape.color;
            const { type, rotation } = shape;
            const alpha = Math.min(1, depth * 1.2);

            ctx.save();
            ctx.translate(screenX, screenY);
            ctx.rotate(rotation);
            ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
            ctx.lineWidth = Math.max(1, size * 0.05);

            switch (type) {
                case 'cube': {
                    // Draw a wireframe cube (front and back squares connected)
                    const half = size / 2;
                    const offset = size * 0.3;
                    // Front square
                    ctx.strokeRect(-half, -half, size, size);
                    // Back square
                    ctx.strokeRect(-half + offset, -half + offset, size, size);
                    // Connect corners
                    ctx.beginPath();
                    ctx.moveTo(-half, -half); ctx.lineTo(-half + offset, -half + offset);
                    ctx.moveTo(half, -half); ctx.lineTo(half + offset, -half + offset);
                    ctx.moveTo(-half, half); ctx.lineTo(-half + offset, half + offset);
                    ctx.moveTo(half, half); ctx.lineTo(half + offset, half + offset);
                    ctx.stroke();
                    break;
                }

                case 'sphere':
                    // Draw wireframe sphere (circles)
                    ctx.beginPath();
                    ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
                    ctx.stroke();
                    // Horizontal ellipse
                    ctx.beginPath();
                    ctx.ellipse(0, 0, size / 2, size / 4, 0, 0, Math.PI * 2);
                    ctx.stroke();
                    // Vertical ellipse
                    ctx.beginPath();
                    ctx.ellipse(0, 0, size / 4, size / 2, 0, 0, Math.PI * 2);
                    ctx.stroke();
                    break;

                case 'cylinder': {
                    // Draw wireframe cylinder
                    const cylH = size;
                    const cylR = size / 3;
                    // Top ellipse
                    ctx.beginPath();
                    ctx.ellipse(0, -cylH / 2, cylR, cylR / 3, 0, 0, Math.PI * 2);
                    ctx.stroke();
                    // Bottom ellipse
                    ctx.beginPath();
                    ctx.ellipse(0, cylH / 2, cylR, cylR / 3, 0, 0, Math.PI * 2);
                    ctx.stroke();
                    // Side lines
                    ctx.beginPath();
                    ctx.moveTo(-cylR, -cylH / 2); ctx.lineTo(-cylR, cylH / 2);
                    ctx.moveTo(cylR, -cylH / 2); ctx.lineTo(cylR, cylH / 2);
                    ctx.stroke();
                    break;
                }

                case 'pyramid': {
                    // Draw wireframe pyramid
                    const pyrH = size;
                    const pyrBase = size * 0.8;
                    // Base square
                    ctx.strokeRect(-pyrBase / 2, pyrH / 4, pyrBase, pyrBase * 0.5);
                    // Lines to apex
                    ctx.beginPath();
                    ctx.moveTo(-pyrBase / 2, pyrH / 4); ctx.lineTo(0, -pyrH / 2);
                    ctx.moveTo(pyrBase / 2, pyrH / 4); ctx.lineTo(0, -pyrH / 2);
                    ctx.moveTo(-pyrBase / 2, pyrH / 4 + pyrBase * 0.5); ctx.lineTo(0, -pyrH / 2);
                    ctx.moveTo(pyrBase / 2, pyrH / 4 + pyrBase * 0.5); ctx.lineTo(0, -pyrH / 2);
                    ctx.stroke();
                    break;
                }
            }

            // Add glow effect
            ctx.shadowColor = `rgba(${r}, ${g}, ${b}, ${alpha * 0.5})`;
            ctx.shadowBlur = size * 0.3;

            ctx.restore();
        };

        initStars();

        // Animation loop
        const animate = () => {
            const rect = canvas.getBoundingClientRect();
            const centerX = rect.width / 2;
            const centerY = rect.height / 2;

            // Clear with semi-transparent black for trail effect
            ctx.fillStyle = 'rgba(10, 12, 16, 0.15)';
            ctx.fillRect(0, 0, rect.width, rect.height);

            // Draw stars
            starsRef.current.forEach((star) => {
                star.z -= speed;

                if (star.z <= 0) {
                    star.x = Math.random() * rect.width - centerX;
                    star.y = Math.random() * rect.height - centerY;
                    star.z = 1000;
                    star.color = STAR_COLORS[Math.floor(Math.random() * STAR_COLORS.length)];
                }

                const perspective = 300 / star.z;
                const screenX = star.x * perspective + centerX;
                const screenY = star.y * perspective + centerY;
                const depth = 1 - star.z / 1000;
                const size = star.size * perspective * 0.5;
                const { r, g, b } = star.color;

                if (screenX >= 0 && screenX <= rect.width && screenY >= 0 && screenY <= rect.height) {
                    const gradient = ctx.createRadialGradient(
                        screenX, screenY, 0,
                        screenX, screenY, size * 3
                    );

                    gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 1)`);
                    gradient.addColorStop(0.3, `rgba(${r}, ${g}, ${b}, ${depth * 0.8})`);
                    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

                    ctx.beginPath();
                    ctx.arc(screenX, screenY, size * 3, 0, Math.PI * 2);
                    ctx.fillStyle = gradient;
                    ctx.fill();

                    ctx.beginPath();
                    ctx.arc(screenX, screenY, size * 0.8, 0, Math.PI * 2);
                    const coreR = Math.min(255, r + 80);
                    const coreG = Math.min(255, g + 80);
                    const coreB = Math.min(255, b + 80);
                    ctx.fillStyle = `rgba(${coreR}, ${coreG}, ${coreB}, ${depth})`;
                    ctx.fill();
                }
            });

            // Draw and update shapes
            shapesRef.current.forEach((shape, index) => {
                shape.z -= speed * 0.8; // Shapes move slightly slower
                shape.rotation += shape.rotationSpeed;

                if (shape.z <= 0) {
                    shapesRef.current[index] = createShape(rect.width, rect.height, true);
                    return;
                }

                const perspective = 300 / shape.z;
                const screenX = shape.x * perspective + centerX;
                const screenY = shape.y * perspective + centerY;
                const depth = 1 - shape.z / 1000;
                const size = shape.size * perspective;

                if (screenX >= -100 && screenX <= rect.width + 100 && screenY >= -100 && screenY <= rect.height + 100) {
                    drawShape(shape, screenX, screenY, size, depth);
                }
            });

            animationRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            cancelAnimationFrame(animationRef.current);
        };
    }, [starCount, speed]);

    return (
        <canvas
            ref={canvasRef}
            className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
            style={{ background: 'transparent' }}
        />
    );
}

